import * as Sentry from '@sentry/vue'

/**
 * Convert time between string formats and seconds
 *
 * @param {string|number} value - Value to convert
 * @param {string} unitIn - Source unit ('hh:mm:ss', 'hh:mm', 'mm:ss', or 's')
 * @param {string} unitOut - Target unit ('hh:mm:ss', 'hh:mm', 'mm:ss', or 's')
 * @param {boolean} returnString - If true, return formatted string
 * @returns {object|string}
 */
export const convertTime = (value = 0, unitIn = 'hh:mm:ss', unitOut = 's', returnString = false) => {
    unitOut = unitOut ?? 's'

    let time = {
        value: value,
        unit: unitIn,
    }

    if (unitIn !== unitOut) {
        switch (unitIn) {
            case 'hh:mm:ss':
                if (unitOut === 's') {
                    const [hours, minutes, seconds] = value.split(':').map((v) => parseInt(v))
                    time.value = hours * 3600 + minutes * 60 + seconds
                } else {
                    logUnsupportedConversion(unitIn, unitOut)
                }
                break

            case 'hh:mm':
                if (unitOut === 's') {
                    const [hours, minutes] = value.split(':').map((v) => parseInt(v))
                    time.value = hours * 3600 + minutes * 60
                } else {
                    logUnsupportedConversion(unitIn, unitOut)
                }
                break

            case 'mm:ss':
                if (unitOut === 's') {
                    const [minutes, seconds] = value.split(':').map((v) => parseInt(v))
                    time.value = minutes * 60 + seconds
                } else {
                    logUnsupportedConversion(unitIn, unitOut)
                }
                break

            case 's':
                time.value = secondsToFormat(value, unitOut)
                if (time.value === null) {
                    logUnsupportedConversion(unitIn, unitOut)
                    time.value = value
                }
                break
        }
        time.unit = unitOut
    }

    return returnString ? `${time.value} ${time.unit}` : time
}

/**
 * Convert seconds to time format string
 */
const secondsToFormat = (seconds, format) => {
    const date = new Date(seconds * 1000)
    const iso = date.toISOString()

    switch (format) {
        case 'hh:mm:ss':
            return seconds < 3600
                ? '00:' + iso.slice(14, 19)
                : iso.slice(11, 19)

        case 'hh:mm':
            return seconds < 3600
                ? '00:' + iso.slice(14, 16)
                : iso.slice(11, 16)

        case 'mm:ss':
            if (seconds < 3600) {
                return iso.slice(14, 19)
            }
            // More than 1 hour: show total minutes
            const minutes = Math.round(seconds / 60)
            return `${minutes}:${iso.slice(17, 19)}`

        default:
            return null
    }
}

/**
 * Log unsupported conversion warning
 */
const logUnsupportedConversion = (unitIn, unitOut) => {
    const warningMessage = `Time conversion from ${unitIn} to ${unitOut} not developed`
    console.warn(warningMessage)
    Sentry.captureMessage(warningMessage)
}
